// Árvore de endpoints na sidebar. Cada endpoint expande mostrando teams/agents
// buscados dinamicamente via /teams + /agents. Token solicitado inline quando
// faltante. Click num team/agent define-o como ativo.

import { useState } from 'preact/hooks';
import { endpoints, active } from '../store/global';
import { upsertEndpoint, removeEndpoint, setActive } from '../lib/auth';
import { listTeams, listAgents } from '../lib/api';
import type { Endpoint } from '../lib/auth';
import type { TeamSummary, AgentSummary } from '../types/agno-api';

export function EndpointTree() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState(false);

  const toggle = (url: string) => setExpanded((p) => ({ ...p, [url]: !p[url] }));

  return (
    <div class="px-2">
      <div class="flex items-center justify-between px-2 py-2">
        <span class="text-xs text-muted-foreground font-medium">Endpoints</span>
        <button
          class="inline-flex items-center justify-center size-6 rounded-md text-muted-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={() => setAdding(true)}
          title="Adicionar endpoint"
        >
          <i class="ph ph-plus text-sm" />
        </button>
      </div>

      <div class="space-y-0.5">
        {endpoints.value.map((ep) => (
          <EndpointNode
            key={ep.url}
            endpoint={ep}
            expanded={!!expanded[ep.url]}
            onToggle={() => toggle(ep.url)}
          />
        ))}
        {adding && <AddEndpoint onDone={() => setAdding(false)} />}
      </div>
    </div>
  );
}

function EndpointNode({
  endpoint: ep,
  expanded,
  onToggle,
}: {
  endpoint: Endpoint;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [tokenDraft, setTokenDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [askingToken, setAskingToken] = useState(false);

  const needsToken = !ep.token;

  const refresh = async (tokenOverride?: string) => {
    const t = tokenOverride ?? ep.token;
    if (!t) {
      setAskingToken(true);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [teams, agents] = await Promise.all([
        listTeams({ baseUrl: ep.url, token: t }).catch((e) => {
          throw e;
        }),
        listAgents({ baseUrl: ep.url, token: t }).catch(() => [] as AgentSummary[]),
      ]);
      const updated: Endpoint = {
        ...ep,
        token: t,
        catalog: {
          teams: teams as TeamSummary[],
          agents: agents as AgentSummary[],
          fetchedAt: Date.now(),
        },
      };
      endpoints.value = upsertEndpoint(updated);
      setAskingToken(false);
      setTokenDraft('');
    } catch (e) {
      setError((e as Error).message.slice(0, 100));
    } finally {
      setLoading(false);
    }
  };

  const remove = () => {
    if (!confirm(`Remover endpoint ${ep.url}?`)) return;
    endpoints.value = removeEndpoint(ep.url);
    if (active.value?.endpointUrl === ep.url) {
      active.value = null;
      setActive(null);
    }
  };

  const selectTarget = (id: string, isTeam: boolean) => {
    const ref = { endpointUrl: ep.url, targetId: id, isTeam };
    active.value = ref;
    setActive(ref);
  };

  const isActiveEndpoint = active.value?.endpointUrl === ep.url;
  const teams = ep.catalog?.teams ?? [];
  const agents = ep.catalog?.agents ?? [];
  const hostShort = ep.url.replace(/^https?:\/\//, '').replace(/\.blips-labs\.com$/, '');

  return (
    <div>
      <div
        class={`group flex items-center gap-1.5 px-1.5 py-1 rounded-sm cursor-pointer hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring ${
          isActiveEndpoint ? 'bg-sidebar-accent text-sidebar-accent-foreground' : 'text-sidebar-foreground'
        }`}
        onClick={onToggle}
      >
        {expanded ? (
          <i class="ph-bold ph-caret-down text-sm text-muted-foreground shrink-0" />
        ) : (
          <i class="ph ph-caret-right text-sm text-muted-foreground shrink-0" />
        )}
        <span class={`text-sm flex-1 min-w-0 truncate ${isActiveEndpoint ? 'font-medium' : ''}`}>
          {ep.label || hostShort}
        </span>
        {needsToken && <i class="ph ph-key text-xs text-warning shrink-0" />}
        <button
          class="inline-flex items-center justify-center size-5 rounded-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive opacity-0 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-destructive/40 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            remove();
          }}
          title="Remover"
        >
          <i class="ph ph-trash text-xs" />
        </button>
      </div>

      {expanded && (
        <div class="ml-4 pl-2 border-l border-border">
          {askingToken || needsToken ? (
            <div class="py-1.5 px-1 space-y-1.5">
              <p class="text-xs text-muted-foreground">Informe o Bearer token deste endpoint.</p>
              <div class="flex gap-1">
                <input
                  type="password"
                  class="flex-1 h-7 px-2 text-xs rounded-md border border-input bg-transparent font-mono shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
                  placeholder="agent-..."
                  value={tokenDraft}
                  onInput={(e) => setTokenDraft((e.target as HTMLInputElement).value)}
                  onKeyDown={(e) => e.key === 'Enter' && refresh(tokenDraft)}
                  autoFocus
                />
                <button
                  class="inline-flex items-center justify-center gap-2 h-7 px-2 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
                  onClick={() => refresh(tokenDraft)}
                  disabled={loading || !tokenDraft.trim()}
                >
                  {loading ? <i class="ph ph-spinner text-xs animate-spin" /> : 'OK'}
                </button>
              </div>
              {error && (
                <p class="text-xs text-destructive flex items-start gap-1">
                  <i class="ph ph-warning-circle text-xs shrink-0 mt-px" />
                  {error}
                </p>
              )}
            </div>
          ) : (
            <>
              {teams.length === 0 && agents.length === 0 && !loading && (
                <button
                  class="w-full text-left text-xs text-muted-foreground px-2 py-1 rounded-sm hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring"
                  onClick={() => refresh()}
                >
                  Carregar teams e agents
                </button>
              )}
              {loading && (
                <div class="text-xs text-muted-foreground px-2 py-1 flex items-center gap-1">
                  <i class="ph ph-spinner text-xs animate-spin" /> Carregando…
                </div>
              )}

              {teams.length > 0 && (
                <div class="py-0.5">
                  {teams.map((t) => (
                    <TargetRow
                      key={t.id}
                      icon={<i class="ph ph-tree-structure text-sm" />}
                      label={t.name || t.id}
                      sub="team"
                      active={
                        isActiveEndpoint && active.value?.targetId === t.id && active.value?.isTeam
                      }
                      onClick={() => selectTarget(t.id, true)}
                    />
                  ))}
                </div>
              )}
              {agents.length > 0 && (
                <div class="py-0.5">
                  {agents.map((a) => (
                    <TargetRow
                      key={a.id}
                      icon={<i class="ph ph-user text-sm" />}
                      label={a.name || a.id}
                      sub="agent"
                      active={
                        isActiveEndpoint && active.value?.targetId === a.id && !active.value?.isTeam
                      }
                      onClick={() => selectTarget(a.id, false)}
                    />
                  ))}
                </div>
              )}

              {ep.catalog && (
                <button
                  class="text-xs text-muted-foreground hover:text-foreground px-2 py-0.5 rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring"
                  onClick={() => refresh()}
                >
                  Atualizar
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TargetRow({
  icon,
  label,
  sub,
  active,
  onClick,
}: {
  icon: preact.ComponentChildren;
  label: string;
  sub: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      class={`w-full flex items-center gap-1.5 px-2 py-1 rounded-sm text-left text-sm hover:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring ${
        active ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium' : 'text-sidebar-foreground'
      }`}
      onClick={onClick}
    >
      <span class={`shrink-0 ${active ? 'text-sidebar-accent-foreground' : 'text-muted-foreground'}`}>{icon}</span>
      <span class="flex-1 min-w-0 truncate">{label}</span>
      <span class="text-xs text-muted-foreground font-mono">{sub}</span>
      {active && <span class="size-1.5 rounded-full bg-primary shrink-0" />}
    </button>
  );
}

function AddEndpoint({ onDone }: { onDone: () => void }) {
  const [url, setUrl] = useState('https://');
  const [label, setLabel] = useState('');

  const save = () => {
    if (!url.trim() || !url.startsWith('http')) return;
    endpoints.value = upsertEndpoint({ url: url.trim(), label: label.trim() || undefined });
    onDone();
  };

  return (
    <div class="p-2 space-y-1.5 rounded-lg border border-border bg-card text-card-foreground shadow-sm">
      <input
        class="w-full h-7 px-2 text-xs rounded-md border border-input bg-transparent font-mono shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
        placeholder="https://nome.blips-labs.com"
        value={url}
        onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
        autoFocus
      />
      <input
        class="w-full h-7 px-2 text-xs rounded-md border border-input bg-transparent shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring"
        placeholder="Rótulo (opcional)"
        value={label}
        onInput={(e) => setLabel((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => e.key === 'Enter' && save()}
      />
      <div class="flex gap-1">
        <button
          class="inline-flex flex-1 items-center justify-center gap-2 h-7 rounded-md bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 disabled:pointer-events-none"
          onClick={save}
        >
          Adicionar
        </button>
        <button
          class="inline-flex items-center justify-center gap-2 h-7 px-2 rounded-md bg-background text-foreground border border-input shadow-xs text-xs hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          onClick={onDone}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
